// Russian for the PUBLIC namespace (Module 09 §4.10) — the only strings a
// person outside the tenant reads, in the language KazKabel's customers
// actually buy cable in.
//
// COMMERCIAL-CORRESPONDENCE REGISTER, not app Russian. A procurement manager at
// a construction firm reads «коммерческое предложение» (or «КП»), «счёт»,
// «отсрочка», «отгрузка» — the vocabulary of the документооборот they already
// run. So:
//
//   offer / quote        → «предложение» (the heading says «Предложение
//                          Q-2026-00042»); «КП» is the abbreviation everyone
//                          says out loud but reads as jargon in a heading a
//                          first-time recipient sees.
//   accept               → «принять» — the verb on the button, not
//                          «подтвердить» (which is what we say to the customer
//                          AFTER, about the order: «Заказ подтверждён»).
//   valid until          → «действительно до» — the phrase already printed at
//                          the bottom of every Kazakh commercial offer.
//   VAT                  → «НДС», never «налог».
//   order tracking       → «статус заказа»; the steps are the four words a
//                          logistics coordinator uses: «Подтверждён», «В
//                          производстве», «Готов», «Отгружен», «Доставлен».
//
// NOTHING INTERNAL LEAKS THROUGH THE TRANSLATION EITHER. There is no Russian
// here for margin, guard, tier or revision, because there is no English for
// them in this namespace — a translator adding a helpful «см. ревизию» would
// be adding a concept the customer is not entitled to.
//
// COUNTED NOUNS ARE NOT AGREED WITH THEIR NUMBER (D-030): «Дней: 12» is correct
// for 1, 2 and 5 alike, where «12 дней / 2 дня / 1 день» needs three branches a
// flat dictionary cannot express.

import type { PublicDict } from "./public.i18n.js";

export const publicRuDict: PublicDict = {
  shell: {
    loading: "Загрузка…",
    retry: "Повторить",
    requestId: "Код обращения",
    poweredBy: "Работает на Assessly",
  },

  quote: {
    heading: "Предложение {{quoteNo}}",
    forCustomer: "Для компании {{customer}}",
    from: "От компании {{seller}}",
    contactManager: "Связаться с менеджером",
    yourManager: "Ваш менеджер — {{name}}.",
    colItem: "Наименование",
    colQty: "Количество",
    colUnitPrice: "Цена за единицу",
    colTotal: "Сумма",
    deliveryBy: "Поставка до {{date}}, подтверждается при принятии",
    subtotal: "Сумма без НДС",
    vatExclusive: "НДС {{rate}}%",
    vatInclusive: "В том числе НДС {{rate}}%",
    total: "Итого",
    validUntil: "Действительно до {{date}}",
    daysLeft: "Дней осталось: {{count}}",
    lastDay: "Последний день, чтобы принять",
    note: "Комментарий",
    pdf: "Скачать предложение (PDF)",
    accept: "Принять предложение",
    decline: "Отклонить",
    accepting: "Принимаем…",
    confirmTitle: "Принять предложение?",
    confirmBody: "Мы сразу зарезервируем товар и подтвердим заказ.",
    confirmYes: "Да, принять",
    confirmNo: "Пока нет",
    declineTitle: "Отклонить предложение?",
    declineBody: "Можете указать причину — так мы точнее посчитаем в следующий раз.",
    declineWhyPrice: "Цена",
    declineWhyLeadTime: "Срок поставки",
    declineWhyStock: "Нужно раньше, чем есть в наличии",
    declineWhyOther: "Другое",
    declineSkip: "Просто отклонить",
    declineYes: "Отклонить предложение",
    declined: "Спасибо — мы передали менеджеру.",
    declineFailed: "Не удалось сохранить ответ. Попробуйте ещё раз.",
    acceptFailed: "Сейчас не получилось принять предложение. Попробуйте ещё раз.",
    acceptGoneTitle: "Это предложение уже принято",
    acceptedTitle: "Заказ подтверждён",
    acceptedNumber: "Заказ {{orderNo}}",
    acceptedOn: "Принято {{date}}",
    trackOrder: "Статус заказа",
    expiredTitle: "Срок предложения истёк",
    expiredOn: "Оно действовало до {{date}}.",
    // D-268 ruling 3: a quote WITHDRAWN by hand never lapsed, so it has no
    // `expired_on` and this title replaces `expiredTitle` — «истёк» would claim
    // a deadline that never passed.
    withdrawnTitle: "Предложение больше не действует",
    expiredAsk: "Попросите менеджера прислать новое — это занимает минуту.",
    supersededTitle: "Есть более новая версия предложения",
    supersededBody: "Цены на этой странице устарели.",
    supersededOn: "Новое предложение отправлено {{date}}.",
    supersededAsk: "Проверьте то сообщение или попросите менеджера прислать его заново.",
    notFoundTitle: "Ссылка не работает",
    notFoundBody:
      "Возможно, она набрана с ошибкой или её уже заменили. Проверьте сообщение, в котором она пришла, или спросите отправителя.",
    errorTitle: "Не удалось открыть страницу",
    errorBody:
      "Что-то пошло не так на нашей стороне. С предложением всё в порядке — попробуйте ещё раз.",
  },

  track: {
    heading: "Заказ {{orderNo}}",
    from: "От компании {{seller}}",
    statusHeading: "Ход выполнения",
    stepConfirmed: "Подтверждён",
    stepInProduction: "В производстве",
    stepReady: "Готов",
    stepShipped: "Отгружен",
    stepDelivered: "Доставлен",
    stepPending: "Ещё не пройден",
    stepDone: "Пройден",
    promiseDate: "Готовность к {{date}}",
    promisePending: "Менеджер уточнит дату — она появится здесь, как только будет определена.",
    colItem: "Наименование",
    colQty: "Количество",
    colShipped: "Отгружено",
    colUnitPrice: "Цена за единицу",
    colTotal: "Сумма",
    total: "Итого",
    stagesPending:
      "Обновления появятся здесь по мере продвижения заказа. О каждом изменении мы также напишем вам.",
    shipmentsHeading: "Отгрузки",
    shipmentNo: "Отгрузка {{no}}",
    shipmentShipped: "Отгружено",
    shipmentCarrier: "Перевозчик",
    shipmentTrackingRef: "Номер для отслеживания",
    shipmentDelivery: "Доставка",
    shipmentOnTheWay: "В пути",
    shipmentDeliveredAt: "Доставлено {{when}}",
    deliveredBy: {
      customer: "подтверждено вами",
      carrier: "подтверждено перевозчиком",
      auto: "подтверждено автоматически",
    },
    confirmReceivedTitle: "Вы получили этот заказ?",
    confirmReceived: "Да, получил(а)",
    confirmReceivedAsk: "Подтвердите, что товар прибыл. Ваш менеджер узнает, что заказ выполнен.",
    confirmReceivedYes: "Подтвердить",
    confirmReceivedNo: "Пока нет",
    confirmReceivedDone: "Спасибо — отмечено как доставлено.",
    confirmReceivedFailed: "Не удалось сохранить ответ. Попробуйте ещё раз.",
    contactManager: "Написать менеджеру",
    notFoundTitle: "Ссылка не работает",
    notFoundBody:
      "Возможно, она набрана с ошибкой. Проверьте сообщение, в котором она пришла, или спросите отправителя.",
    errorTitle: "Не удалось открыть страницу",
    errorBody:
      "Что-то пошло не так на нашей стороне. С заказом всё в порядке — попробуйте ещё раз.",
  },
};
